/**
 * Thumbnails of a photo turned in the viewer. The viewer saves the turn as the
 * file's `rotation` (degrees clockwise, a multiple of 90). A thumbnail shows it
 * by turning its <img> only, never the box around it, so the box keeps its
 * size and place.
 */

const QUARTER_TURNS = [0, 90, 180, 270];

const TURN_STYLES = {
  90: Object.freeze({ transform: 'rotate(90deg)' }),
  180: Object.freeze({ transform: 'rotate(180deg)' }),
  270: Object.freeze({ transform: 'rotate(270deg)' }),
};

// One style object per turn and box size, so a memoized thumbnail keeps
// getting the same one.
const boxStyles = new Map();

/**
 * A saved rotation as 0, 90, 180 or 270: a number in a string, a turn to the
 * left (-90) and whole extra turns (450) fold in; anything that is not a
 * number of degrees is 0.
 * @param {*} value
 * @returns {number} 0, 90, 180 or 270
 */
export function normalizeRotation(value) {
  const degrees =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof degrees !== 'number' || !Number.isFinite(degrees)) return 0;
  const quarterTurns = Math.round(degrees / 90) % 4;
  return QUARTER_TURNS[(quarterTurns + 4) % 4];
}

/**
 * The inline style that turns a thumbnail's <img>, or undefined when the photo
 * is not turned.
 *
 * Without `box`, in a square box, or for a half turn, a plain rotate() will do:
 * the turned picture covers the same area. A quarter turn in a box that is not
 * square (the AI panel's 42×28 avatar) also swaps the picture's width and
 * height and centres it, so that once turned it covers the box. Such a box
 * must be `position: relative` with `overflow: hidden`, as an MUI Avatar is.
 * @param {*} rotation the saved rotation, as normalizeRotation takes it
 * @param {{width: number, height: number}} [box] the box's size in px
 * @returns {Object|undefined}
 */
export function getRotatedThumbnailStyle(rotation, box) {
  const degrees = normalizeRotation(rotation);
  if (!degrees) return undefined;
  if (degrees === 180 || !box || box.width === box.height) {
    return TURN_STYLES[degrees];
  }
  const key = `${degrees}:${box.width}x${box.height}`;
  if (!boxStyles.has(key)) {
    boxStyles.set(
      key,
      Object.freeze({
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: box.height,
        height: box.width,
        maxWidth: 'none',
        transform: `translate(-50%, -50%) rotate(${degrees}deg)`,
      })
    );
  }
  return boxStyles.get(key);
}
